<script setup lang="ts">
import { computed, onMounted, nextTick, capitalize } from 'vue';
import { ref } from 'vue';
import "/node_modules/flag-icons/css/flag-icons.min.css";

export interface CountryOption {
    code: string,
    name: string
}

const EmptyStr = '';
const EnterKey = 'Enter';

const props = defineProps<{
    options: Array<CountryOption>,
    default?: CountryOption,
    tabindex: number | string |undefined,
}>();

const emit = defineEmits(['input', 'search-updated', 'key-focus']);
const open = ref(false);
const selected: any = ref(null);
const searchModel = defineModel<string>();
const preValue = ref('');
const defaultLength = ref(0);

onMounted(load);

function load() {
    selected.value = props.default
        ? props.default
        : props.options.length > 0
            ? props.options[0]
            : null;
    defaultLength.value = props.options.length;
    emit("input", selected);
}

const optionsModel = computed(() => {
    return props.options;
});

function searchFilter(newValue: Event) {
    emit('search-updated', searchModel.value);
}

function keyDown(event: KeyboardEvent) {
    if (!open.value) {
        openDropdown();
        searchModel.value = `${searchModel.value ? searchModel.value : ''}${event.key}`;
        emit('key-focus', event.key);
    }
}

function searchKeyDown(event: KeyboardEvent) { 
    if (event.key === EnterKey) {
        if (searchModel.value && searchModel.value.length > 0) {            
            if (props.options.length > 0) {
                const firstOption = props.options[0];

                if (firstOption) {         
                    selectCountry(firstOption);    
                }                 
            }
        }
    } else {
        capitalizeFirstLetter();
    }

    fillAutoComplete();
}

function capitalizeFirstLetter() {
    const value = searchModel.value;
    if (value && value.length > 0) {    
        searchModel.value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }
}

function fillAutoComplete() {
    //auto preview complete
    if (searchModel.value && searchModel.value.length > 0) {     
        if (optionsModel.value.length > 0 && optionsModel.value.length < defaultLength.value) {
            const firstOption = optionsModel.value[0];
            preValue.value = firstOption.name;
        } else {
            preValue.value = EmptyStr;
        }
    } else {
        preValue.value = EmptyStr;
    }
}

function closeOrResetSearch() {
    if (searchModel.value && searchModel.value.length > 0) {
        searchModel.value = EmptyStr;        
        preValue.value = EmptyStr;
        emit('search-updated', searchModel.value);        
    }

    closeDropdown();
}

function unfocusSearch() {
    setTimeout(closeDropdown, 300);
}

function openDropdown() {
    if (!open.value) {   
        searchModel.value = EmptyStr;
        preValue.value = EmptyStr;  
        open.value = true;
        nextTick().then(() => {
            searchModel.value = EmptyStr;
            document.getElementById("searchBox")!.focus();
        });
    }
}

function closeDropdown() {
    if (open.value) {
        open.value = false;
    }
}

function selectCountry(option: CountryOption) {   
    selected.value = option;
    emit('input', option);
    closeDropdown();
}

</script>

<template>
    <div class="country-select" :tabindex="tabindex" @keypress="keyDown">
        <div v-if="!open" class="selected" :class="{ open: open }" @click="openDropdown()">
            <span class="fi fir" :class="`fi-${selected?.code ? selected.code.toLowerCase() : ''}`"></span>
            {{ selected?.name }}
        </div>
        <div v-if="open" class="country-select-container">
            <div class="selected search">
                <i class="search-icon fa fa-search" aria-hidden="true"></i>
                <input
                    id="searchBox"
                    v-model="searchModel" 
                    class="search-box" 
                    :placeholder="$t('country.search')"
                    type="text"
                    maxlength="40" 
                    @input="searchFilter($event)" 
                    @keyup="searchKeyDown" 
                    @blur="unfocusSearch" />
                <span class="search-auto">{{ preValue }}</span>
                <span class="search-close fa-solid fa-xmark" @click="closeOrResetSearch"></span>
            </div>
            <div class="items" :class="{ selectHide: !open }">
                <div v-for="(option, i) of optionsModel" :key="i" @click="selectCountry(option)">
                    <span class="fi fir" :class="`fi-${option.code ? option.code.toLowerCase() : ''}`"></span>
                    {{ option.name }}
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
@import './country-select.scss';
</style>
